import { useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiLoginRequest } from '@/authConfig';
import type { SilentRequest } from '@azure/msal-browser';
import { authTelemetry } from '@/app/services/authTelemetry';

// NOTE: This token refresh mechanism works with the 'offline_access' scope for longer sessions.
// The offline_access scope enables refresh tokens which allow sessions to persist much longer.

const TOKEN_REFRESH_INTERVAL = 50 * 60 * 1000; // 50 minutes (increased with offline_access)
const TOKEN_CHECK_INTERVAL = 10 * 60 * 1000; // Check every 10 minutes (less frequent)
const ACTIVITY_REFRESH_THRESHOLD = 30 * 60 * 1000; // 30 minutes since last activity

export const useTokenRefresh = () => {
  const { instance, accounts } = useMsal();
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshRef = useRef<number>(Date.now());
  const lastActivityRef = useRef<number>(Date.now());
  const isRefreshingRef = useRef<boolean>(false);

  const resolveAccount = () => {
    const activeAccount = instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const [firstAccount] = instance.getAllAccounts();
    if (firstAccount) {
      instance.setActiveAccount(firstAccount);
      return firstAccount;
    }

    return null;
  };

  useEffect(() => {
    if (!accounts || accounts.length === 0) {
      return;
    }

    // Track user activity to optimize refresh timing (debounced)
    let activityTimeout: NodeJS.Timeout | null = null;
    const updateActivity = () => {
      lastActivityRef.current = Date.now();

      // Debounce activity tracking to avoid spam
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }

      activityTimeout = setTimeout(() => {
        authTelemetry.trackUserActivity('mouse'); // Track activity for telemetry
      }, 5000); // Only track activity every 5 seconds max
    };

    const refreshToken = async (forceRefresh = false) => {
      if (isRefreshingRef.current) {
        return; // Prevent concurrent silent token requests (causes MSAL iframe conflict)
      }
      isRefreshingRef.current = true;
      try {
        const account = resolveAccount();

        if (!account) {
          return;
        }

        const request: SilentRequest = {
          ...apiLoginRequest,
          account: account,
          forceRefresh
        };

        const response = await instance.acquireTokenSilent(request);

        if (response) {
          const now = Date.now();
          const timeSinceLastRefresh = now - lastRefreshRef.current;

          // Only track as a refresh if it's been more than 30 seconds since last refresh
          // or if it's a forced refresh. This prevents tracking cached token retrievals.
          const shouldTrackRefresh = forceRefresh || timeSinceLastRefresh > 30000;

          if (shouldTrackRefresh) {
            lastRefreshRef.current = now;
            // Track successful token refresh
            authTelemetry.trackTokenRefreshSuccess(forceRefresh ? 'manual' : 'automatic');
          }

          // Also refresh the ID token to keep user session alive
          try {
            await instance.acquireTokenSilent({
              scopes: ['openid', 'profile'],
              account: account,
              forceRefresh,
              redirectUri: '/blank.html'
            });
          } catch (error) {
            // Only track ID token failures if we tracked the main refresh
            if (shouldTrackRefresh) {
              authTelemetry.trackTokenRefreshFailure(error, 'automatic');
            }
          }
        }
      } catch (error) {
        // Track token refresh failure
        authTelemetry.trackTokenRefreshFailure(error, forceRefresh ? 'manual' : 'automatic');
        // Don't throw - let the normal auth flow handle this
      } finally {
        isRefreshingRef.current = false;
      }
    };

    // Add activity listeners (reduced set to avoid excessive tracking)
    const activityEvents = ['mousedown', 'keypress', 'click']; // Removed mousemove and scroll for less noise
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Initial refresh after a short delay (non-forced to use cached tokens if valid)
    const initialTimeout = setTimeout(() => {
      refreshToken(false);
    }, 30000); // 30 seconds after mount

    // Set up periodic refresh with activity-aware logic
    refreshIntervalRef.current = setInterval(() => {
      const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;

      // Track token expiration patterns
      if (timeSinceLastRefresh >= TOKEN_REFRESH_INTERVAL * 0.9) {
        authTelemetry.trackTokenExpiration('access', TOKEN_REFRESH_INTERVAL - timeSinceLastRefresh);
      }

      // Refresh strategy:
      // • Once the refresh interval has elapsed we renew the token, regardless of recent
      //   user activity. This ensures the session survives for the full period allowed
      //   by the access/refresh-token pair and prevents unexpected log-outs.
      if (timeSinceLastRefresh >= TOKEN_REFRESH_INTERVAL) {
        if (timeSinceLastActivity <= ACTIVITY_REFRESH_THRESHOLD) {
          refreshToken(true);
        } else {
          // User has been inactive but we still perform a refresh to keep the session alive
          refreshToken(true);

          // Track that we refreshed despite inactivity (sampled to reduce noise)
          if (Math.random() < 0.1) { // 10% sampling
            authTelemetry.trackUserActivity('focus');
          }
        }
      }
    }, TOKEN_CHECK_INTERVAL);

    // Refresh on window focus if it's been a while and user is active
    const handleFocus = () => {
      updateActivity(); // Mark as activity
      const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
      // Only refresh if it's been more than 90% of the interval (more conservative)
      if (timeSinceLastRefresh >= TOKEN_REFRESH_INTERVAL * 0.9) {
        refreshToken(false); // Use cached tokens if valid
      }
    };

    window.addEventListener('focus', handleFocus);

    // Refresh on visibility change (when tab becomes visible)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateActivity(); // Mark as activity
        const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
        // Only refresh if it's been more than 90% of the interval (more conservative)
        if (timeSinceLastRefresh >= TOKEN_REFRESH_INTERVAL * 0.9) {
          refreshToken(false); // Use cached tokens if valid
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(initialTimeout);
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      // Clean up activity listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [instance, accounts]);

  return null;
};
